import { create } from '@bufbuild/protobuf'
import {
  QuestionTypeEnum,
  QuestionnaireAnswerInputSchema,
  QuestionnaireAnswerSchema,
  QuestionnaireChoiceSchema,
  QuestionnaireFileSchema,
  QuestionnaireFilesSchema,
  QuestionnaireMultiChoiceSchema,
  type Questionnaire,
  type QuestionnaireAnswer,
  type QuestionnaireAnswerInput,
  type QuestionnaireFile,
  type QuestionnaireItem,
} from '@/gen/cortado/v1/questionnaires_pb'

export type QuestionnaireDraftValue = string | string[] | boolean | QuestionnaireFile[]

export type QuestionnaireDraftValues = Record<string, QuestionnaireDraftValue>

/** Matches the backend attachment limit (MaxAttachmentBytes). */
export const QUESTIONNAIRE_FILE_MAX_BYTES = 23 * 1024 * 1024

/** Matches the backend per-item cap (questionnaire.MaxFilesPerItem). */
export const QUESTIONNAIRE_MAX_FILES = 10

/** How many files a file question accepts: one for FILE_UPLOAD, the cap for FILE_UPLOAD_MULTIPLE. */
export function questionMaxFiles(type: QuestionTypeEnum): number {
  return type === QuestionTypeEnum.FILE_UPLOAD_MULTIPLE ? QUESTIONNAIRE_MAX_FILES : 1
}

export type FilePreviewKind = 'image' | 'pdf' | 'spreadsheet' | 'archive' | 'document'

/** Picks the preview for an uploaded file: a thumbnail for images, an icon for the rest. */
export function filePreviewKind(contentType: string, filename = ''): FilePreviewKind {
  const type = contentType.toLowerCase()
  const ext = filename.toLowerCase().split('.').pop() ?? ''
  if (type.startsWith('image/')) return 'image'
  if (type === 'application/pdf' || ext === 'pdf') return 'pdf'
  if (type === 'text/csv' || type.includes('spreadsheet') || type.includes('excel') || ['csv', 'xls', 'xlsx'].includes(ext)) {
    return 'spreadsheet'
  }
  if (type.includes('zip') || ext === 'zip') return 'archive'
  return 'document'
}

/** File types the attachment library accepts, for the file picker. */
export const QUESTIONNAIRE_FILE_ACCEPT = [
  'image/png', 'image/jpeg', 'image/gif', 'image/webp',
  'application/pdf', 'text/plain', 'text/csv', 'application/zip',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.csv',
].join(',')

export function draftFiles(value: QuestionnaireDraftValue | undefined): QuestionnaireFile[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is QuestionnaireFile => typeof entry === 'object')
}

export function formatFileSize(bytes: bigint | number): string {
  const n = Number(bytes)
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

export function sortedQuestionnaireItems(questionnaire?: Questionnaire | null): QuestionnaireItem[] {
  if (!questionnaire?.items.length) return []
  return [...questionnaire.items].sort((a, b) => a.order - b.order)
}

export function itemHasAnswer(item: QuestionnaireItem): boolean {
  const answer = item.answer?.value
  if (!answer?.case) return false

  switch (answer.case) {
    case 'text':
      return answer.value.trim() !== ''
    case 'boolean':
      return true
    case 'choice':
      return answer.value.optionId.trim() !== ''
    case 'multiChoice':
      return answer.value.choices.length > 0
    case 'files':
      return answer.value.files.length > 0
    default:
      return false
  }
}

export type QuestionWidgetKind =
  | 'text'
  | 'textarea'
  | 'tel'
  | 'email'
  | 'address'
  | 'boolean'
  | 'choice'
  | 'multi'
  | 'files'

export function questionWidgetKind(type: QuestionTypeEnum): QuestionWidgetKind {
  switch (type) {
    case QuestionTypeEnum.PARAGRAPH:
      return 'textarea'
    case QuestionTypeEnum.PHONE:
      return 'tel'
    case QuestionTypeEnum.EMAIL:
      return 'email'
    case QuestionTypeEnum.ADDRESS:
      return 'address'
    case QuestionTypeEnum.BOOLEAN:
      return 'boolean'
    case QuestionTypeEnum.MULTIPLE_CHOICE:
      return 'choice'
    case QuestionTypeEnum.MULTI_SELECT:
      return 'multi'
    case QuestionTypeEnum.FILE_UPLOAD:
    case QuestionTypeEnum.FILE_UPLOAD_MULTIPLE:
      return 'files'
    default:
      return 'text'
  }
}

export function hasQuestionnaireItems(questionnaire?: Questionnaire | null): boolean {
  return sortedQuestionnaireItems(questionnaire).length > 0
}

export function hasUnansweredQuestionnaireItems(questionnaire?: Questionnaire | null): boolean {
  return countUnansweredRequiredQuestions(questionnaire) > 0
}

export function countUnansweredRequiredQuestions(questionnaire?: Questionnaire | null): number {
  return sortedQuestionnaireItems(questionnaire).filter((item) => item.required && !itemHasAnswer(item)).length
}

export type BookingQuestionnaireRailState = {
  visible: boolean
  isComplete: boolean
  unansweredRequired: number
  answered: number
  total: number
}

export function bookingQuestionnaireRailState(
  questionnaire?: Questionnaire | null,
): BookingQuestionnaireRailState {
  const items = sortedQuestionnaireItems(questionnaire)
  const unansweredRequired = items.filter((item) => item.required && !itemHasAnswer(item)).length
  const answered = items.filter(itemHasAnswer).length
  return {
    visible: items.length > 0,
    isComplete: items.length > 0 && unansweredRequired === 0,
    unansweredRequired,
    answered,
    total: items.length,
  }
}

export function draftValuesFromQuestionnaire(questionnaire?: Questionnaire | null): QuestionnaireDraftValues {
  const values: QuestionnaireDraftValues = {}
  for (const item of sortedQuestionnaireItems(questionnaire)) {
    const answer = item.answer?.value
    if (!answer?.case) continue

    switch (answer.case) {
      case 'text':
        values[item.id] = answer.value
        break
      case 'boolean':
        values[item.id] = answer.value
        break
      case 'choice':
        values[item.id] = answer.value.optionId
        break
      case 'multiChoice':
        values[item.id] = answer.value.choices.map((choice) => choice.optionId)
        break
      case 'files':
        values[item.id] = answer.value.files
        break
      default:
        break
    }
  }
  return values
}

/**
 * Formats phone input as (XXX) XXX-XXXX while typing. Non-digits are dropped,
 * a leading US country code 1 is removed, and input stops at ten digits.
 */
export function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '').replace(/^1/, '').slice(0, 10)
  if (digits.length === 0) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/** Matches the backend rule (utils.IsValidPhoneNumber): ten digits, area code 2-9. */
export function isValidPhone(value: string): boolean {
  return /^[2-9]\d{9}$/.test(value.replace(/\D/g, ''))
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

export function questionLabel(item: QuestionnaireItem, index: number): string {
  return item.prompt.trim() || `Question ${index + 1}`
}

/**
 * The format problem with an answer the customer has started, or null.
 * An empty answer has no format problem; requiredFieldError covers that.
 */
export function formatFieldError(item: QuestionnaireItem, raw: QuestionnaireDraftValue | undefined): string | null {
  const text = typeof raw === 'string' ? raw.trim() : ''
  if (text === '') return null
  if (item.type === QuestionTypeEnum.PHONE && !isValidPhone(text)) {
    return 'Enter a 10-digit phone number, like (555) 555-5555'
  }
  if (item.type === QuestionTypeEnum.EMAIL && !isValidEmail(text)) {
    return 'Enter a valid email address, like name@example.com'
  }
  return null
}

export function requiredFieldError(
  item: QuestionnaireItem,
  index: number,
  raw: QuestionnaireDraftValue | undefined,
): string | null {
  if (!item.required) return null
  const label = questionLabel(item, index)

  switch (item.type) {
    case QuestionTypeEnum.MULTI_SELECT:
      return Array.isArray(raw) && raw.some(Boolean) ? null : `${label} is required`
    case QuestionTypeEnum.FILE_UPLOAD:
    case QuestionTypeEnum.FILE_UPLOAD_MULTIPLE:
      return draftFiles(raw).length > 0 ? null : `${label} needs at least one file`
    case QuestionTypeEnum.BOOLEAN:
      return typeof raw === 'boolean' ? null : `${label} is required`
    default:
      return typeof raw === 'string' && raw.trim() !== '' ? null : `${label} is required`
  }
}

/** Errors keyed by item ID; an empty object means the draft can be submitted. */
export function questionnaireFieldErrors(
  items: QuestionnaireItem[],
  values: QuestionnaireDraftValues,
): Record<string, string> {
  const errors: Record<string, string> = {}
  items.forEach((item, index) => {
    const error = requiredFieldError(item, index, values[item.id]) ?? formatFieldError(item, values[item.id])
    if (error) errors[item.id] = error
  })
  return errors
}

function draftValueToAnswer(item: QuestionnaireItem, raw: QuestionnaireDraftValue): QuestionnaireAnswer | null {
  switch (item.type) {
    case QuestionTypeEnum.FREE_FORM:
    case QuestionTypeEnum.PARAGRAPH:
    case QuestionTypeEnum.PHONE:
    case QuestionTypeEnum.EMAIL:
    case QuestionTypeEnum.ADDRESS: {
      const text = typeof raw === 'string' ? raw.trim() : ''
      if (!text) return null
      return create(QuestionnaireAnswerSchema, {
        value: { case: 'text', value: text },
      })
    }
    case QuestionTypeEnum.BOOLEAN: {
      if (typeof raw !== 'boolean') return null
      return create(QuestionnaireAnswerSchema, {
        value: { case: 'boolean', value: raw },
      })
    }
    case QuestionTypeEnum.MULTIPLE_CHOICE: {
      const optionId = typeof raw === 'string' ? raw.trim() : ''
      if (!optionId) return null
      const option = item.options.find((entry) => entry.id === optionId)
      return create(QuestionnaireAnswerSchema, {
        value: {
          case: 'choice',
          value: create(QuestionnaireChoiceSchema, {
            optionId,
            optionLabel: option?.label ?? '',
          }),
        },
      })
    }
    case QuestionTypeEnum.FILE_UPLOAD:
    case QuestionTypeEnum.FILE_UPLOAD_MULTIPLE: {
      const files = draftFiles(raw)
      if (files.length === 0) return null
      return create(QuestionnaireAnswerSchema, {
        value: {
          case: 'files',
          value: create(QuestionnaireFilesSchema, {
            files: files.map((file) => create(QuestionnaireFileSchema, { attachmentId: file.attachmentId })),
          }),
        },
      })
    }
    case QuestionTypeEnum.MULTI_SELECT: {
      const optionIds = Array.isArray(raw) ? raw.filter((entry): entry is string => typeof entry === 'string' && entry !== '') : []
      if (optionIds.length === 0) return null
      return create(QuestionnaireAnswerSchema, {
        value: {
          case: 'multiChoice',
          value: create(QuestionnaireMultiChoiceSchema, {
            choices: optionIds.map((optionId) => {
              const option = item.options.find((entry) => entry.id === optionId)
              return create(QuestionnaireChoiceSchema, {
                optionId,
                optionLabel: option?.label ?? '',
              })
            }),
          }),
        },
      })
    }
    default:
      return null
  }
}

export function answersFromDraftValues(
  items: QuestionnaireItem[],
  values: QuestionnaireDraftValues,
): QuestionnaireAnswerInput[] {
  const answers: QuestionnaireAnswerInput[] = []

  for (const item of items) {
    const raw = values[item.id]
    if (raw === undefined) continue
    const answer = draftValueToAnswer(item, raw)
    if (!answer) continue
    answers.push(create(QuestionnaireAnswerInputSchema, {
      itemId: item.id,
      answer,
    }))
  }

  return answers
}
